import { useState, useEffect } from 'react';
import { Box, Flex, Text, chakra } from '@chakra-ui/react';
import { X, MapPin } from 'lucide-react';
import { DateRangePicker } from '@/components/ui';

function toDateStr(d: Date): string {
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${d.getFullYear()}-${m}-${day}`;
}

export interface Location {
  name: string;
  latitude: number;
  longitude: number;
}

interface FilterModalProps {
  isOpen: boolean;
  onClose: () => void;
  onApply: (filters: {
    startDate: Date | null;
    endDate: Date | null;
    selectedLocations: Location[];
  }) => void;
  initialFilters?: {
    startDate: Date | null;
    endDate: Date | null;
    selectedLocations: Location[];
  };
  onLocationPickerOpen?: () => void;
  isLocationPickerLoading?: boolean;
  tempSelectedLocations?: Location[];
}

export function FilterModal({
  isOpen,
  onClose,
  onApply,
  initialFilters,
  onLocationPickerOpen,
  isLocationPickerLoading = false,
  tempSelectedLocations: externalTempSelectedLocations,
}: FilterModalProps) {
  const [tempStartDate, setTempStartDate] = useState<Date | null>(null);
  const [tempEndDate, setTempEndDate] = useState<Date | null>(null);
  const [tempSelectedLocations, setTempSelectedLocations] = useState<Location[]>([]);

  useEffect(() => {
    if (isOpen) {
      setTempStartDate(initialFilters?.startDate ?? null);
      setTempEndDate(initialFilters?.endDate ?? null);
      setTempSelectedLocations(initialFilters?.selectedLocations ?? []);
    }
  }, [isOpen, initialFilters]);

  useEffect(() => {
    if (externalTempSelectedLocations !== undefined) {
      setTempSelectedLocations(externalTempSelectedLocations);
    }
  }, [externalTempSelectedLocations]);

  const handleApply = () => {
    onApply({
      startDate: tempStartDate,
      endDate: tempEndDate,
      selectedLocations: tempSelectedLocations,
    });
    onClose();
  };

  const handleClear = () => {
    setTempStartDate(null);
    setTempEndDate(null);
    setTempSelectedLocations([]);
  };

  const handleRangeChange = (range: { start: string | null; end: string | null }) => {
    setTempStartDate(range.start ? new Date(range.start) : null);
    setTempEndDate(range.end ? new Date(range.end) : null);
  };

  const formatDateForDisplay = (date: Date | null): string => {
    if (!date) return 'Select date';
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  if (!isOpen) return null;

  return (
    <Flex
      data-modal-overlay
      position="fixed"
      inset="0"
      bg="rgba(0,0,0,0.5)"
      backdropFilter="blur(4px)"
      justifyContent="center"
      alignItems="flex-start"
      zIndex={1000}
      overflowY="auto"
      p="5"
      overscrollBehavior="contain"
      onClick={onClose}
    >
      <Flex
        direction="column"
        bg="white"
        borderRadius="24px"
        w="full"
        maxW={{ base: '500px', md: '800px', lg: '900px' }}
        maxH="90vh"
        boxShadow="0 25px 50px -12px rgba(0,0,0,0.25)"
        overflow="hidden"
        my="auto"
        position="relative"
        zIndex={1001}
        onClick={(e: React.MouseEvent) => e.stopPropagation()}
      >
        <Flex
          as="header"
          justifyContent="space-between"
          alignItems="center"
          p={{ base: '4', md: '6' }}
          borderBottom="1px solid"
          borderColor="rgba(199,199,204,0.3)"
          flexShrink={0}
        >
          <Text as="h2" m="0" fontSize="xl" fontWeight="semibold" color="volyx.text" fontFamily="body">
            Filters
          </Text>
          <chakra.button
            onClick={onClose}
            aria-label="Close"
            p="1.5"
            bg="transparent"
            border="none"
            cursor="pointer"
            display="flex"
            alignItems="center"
            justifyContent="center"
            borderRadius="full"
            _disabled={{ opacity: 0.5, cursor: 'not-allowed' }}
          >
            <X size={24} color="var(--color-text)" />
          </chakra.button>
        </Flex>

        <Box
          data-modal-body
          p={{ base: '4', md: '6' }}
          overflowY="auto"
          flex="1"
          overscrollBehavior="contain"
          css={{ '& > * + *': { marginTop: '24px' } }}
        >
          <Box>
            <DateRangePicker
              value={{
                start: tempStartDate ? toDateStr(tempStartDate) : null,
                end: tempEndDate ? toDateStr(tempEndDate) : null,
              }}
              onChange={handleRangeChange}
              minDate={new Date()}
              startPlaceholder="Start Date"
              endPlaceholder="End Date"
            />
            {tempStartDate && tempEndDate && (
              <Text mt="3" textAlign="center" color="#64748b" fontWeight="medium" fontFamily="body" fontSize="sm" mb="0">
                {formatDateForDisplay(tempStartDate)} – {formatDateForDisplay(tempEndDate)}
              </Text>
            )}
          </Box>

          <Box>
            <chakra.label
              display="block"
              fontSize="sm"
              fontWeight="semibold"
              color="#6b7280"
              mb="2"
              fontFamily="body"
            >
              Location
            </chakra.label>
            <chakra.button
              w="full"
              h="14"
              bg="volyx.lightGray"
              borderRadius="20px"
              border="0"
              px="4"
              display="flex"
              alignItems="center"
              gap="3"
              cursor="pointer"
              fontFamily="body"
              textAlign="left"
              boxShadow="none"
              _hover={{ bg: '#E5E5EA' }}
              _disabled={{ opacity: 0.6, cursor: 'not-allowed' }}
              outline="none"
              _focus={{ outline: 'none' }}
              onClick={onLocationPickerOpen}
              disabled={isLocationPickerLoading}
            >
              <Box as={MapPin} boxSize="20px" color="volyx.textSecondary" flexShrink={0} />
              <Text
                as="span"
                flex="1"
                fontSize="15px"
                fontFamily="body"
                color={tempSelectedLocations.length === 0 ? 'volyx.textSecondary' : 'volyx.text'}
              >
                {tempSelectedLocations.length === 0
                  ? 'Select locations'
                  : `${tempSelectedLocations.length} location${tempSelectedLocations.length > 1 ? 's' : ''} selected`}
              </Text>
              {tempSelectedLocations.length > 0 && (
                <Text as="span" fontSize="sm" color="volyx.primary" fontWeight="semibold">
                  {tempSelectedLocations.length}
                </Text>
              )}
            </chakra.button>
          </Box>
        </Box>

        <Flex
          justifyContent="flex-end"
          gap="3"
          p={{ base: '4', md: '6' }}
          borderTop="1px solid"
          borderColor="rgba(199,199,204,0.3)"
          flexShrink={0}
          bg="rgba(249,250,251,0.3)"
        >
          <chakra.button
            flex="1"
            px="6"
            py="3"
            borderRadius="20px"
            fontWeight="semibold"
            transition="all 0.3s"
            cursor="pointer"
            border="none"
            outline="none"
            bg="volyx.lightGray"
            color="volyx.text"
            _hover={{ bg: 'volyx.mediumGray', transform: 'scale(1.01)' }}
            onClick={handleClear}
          >
            Clear
          </chakra.button>
          <chakra.button
            flex="1"
            px="6"
            py="3"
            borderRadius="20px"
            fontWeight="semibold"
            transition="all 0.3s"
            cursor="pointer"
            border="none"
            outline="none"
            bg="volyx.primary"
            color="white"
            boxShadow="0 4px 12px rgba(0,0,0,0.08)"
            _hover={{ boxShadow: '0 8px 24px rgba(0,0,0,0.12)', transform: 'translateY(-2px) scale(1.02)' }}
            _disabled={{ opacity: 0.7, cursor: 'not-allowed' }}
            onClick={handleApply}
          >
            Apply
          </chakra.button>
        </Flex>
      </Flex>
    </Flex>
  );
}
